import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Paper,
  Typography,
  Box
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useDispatch, useSelector } from 'react-redux';
import { addStockReturn, updateStockReturn } from '../../redux/actions/stockReturn/stockReturnAction';
import { fetchRawStocks } from '../../redux/actions/rawStock/rawStockAction';
import { fetchEmployees } from '../../redux/actions/employee/employeeAction';
import { fetchImportedInventory } from '../../redux/actions/importedInventory/importedInventoryAction';
import { showError, showSuccess } from '../../utils/alertUtils';
import { fetchStocks } from '../../redux/actions/stock/stockAction';
import { fetchStones } from '../../redux/actions/stone/stoneAction';
import { fetchPurities } from '../../redux/actions/purity/purityAction';
import { fetchColors } from '../../redux/actions/color/colorAction';

const initialForm = {
  emp_code: '',
  batch_no: '',
  stock_code: '',
  stock_name: '',
  stock_type_id: null,
  karatage: '',
  color: '',
  stone_type_id: null,
  qty: '',
  weight: '',
  return_reason: ''
};

const StockReturnForm = ({ onSuccess, editMode = false, initialData = null, onCancel }) => {
  const dispatch = useDispatch();
  const { rawStocks } = useSelector((state) => state.rawStock);
  const { employees } = useSelector((state) => state.employee);
  const { importedInventory } = useSelector((state) => state.importedInventory);
  const { stocks } = useSelector((state) => state.stock); // stock_master
  const { stones } = useSelector((state) => state.stone); // stone_type_master
  const { purities } = useSelector((state) => state.purity); // <-- Add this line to get purities from Redux
  const { colors } = useSelector((state) => state.color); // <-- Add this line to get colors from Redux
  const user = useSelector((state) => state.login.user);
  const [formData, setFormData] = useState(initialData || initialForm);
  const [selectedStock, setSelectedStock] = useState(null);

  useEffect(() => {
    dispatch(fetchRawStocks({ limit: 0, today_only: false }));
    dispatch(fetchEmployees());
    dispatch(fetchImportedInventory());
    dispatch(fetchStocks());
    dispatch(fetchStones());
    dispatch(fetchPurities());
    dispatch(fetchColors());
  }, [dispatch]);

  // Set initial data when in edit mode
  useEffect(() => {
    if (editMode && initialData) {
      setFormData(initialData);
      // Find the stock in rawStocks to set selectedStock
      const stock = rawStocks.find(s => s.stock_code === initialData.stock_code);
      if (stock) {
        setSelectedStock(stock);
      }
    }
  }, [editMode, initialData, rawStocks]);

  const availableStocks = rawStocks;

  // Find the selected batch and its status
  const selectedBatch = importedInventory.find(b => b.batch_number === formData.batch_no);
  const isCompletedBatch = selectedBatch && selectedBatch.batch_no_status === 2;
  // Only allow non-completed batches in the dropdown
  const availableBatches = importedInventory.filter(batch => batch.batch_no_status !== 2);

  const handleStockCodeChange = (event, value) => {
    setSelectedStock(value);
    if (value) {
      setFormData(prev => ({
        ...prev,
        stock_code: value.stock_code,
        stock_name: value.stock_name,
        stock_type_id: value.stock_type_id || null,
        karatage: value.karatage_name,
        karatage_id: value.karatage_id || (purities.find(p => p.name === value.karatage_name)?.id || null),
        color: value.color_name,
        color_id: value.color_id || (colors.find(c => c.name === value.color_name)?.id || null),
        stone_type_id: value.stone_type_id || null,
      }));
    } else {
      setFormData(initialForm);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validation: Only one of emp_code or batch_no must be filled
    if ((formData.emp_code && formData.batch_no) || (!formData.emp_code && !formData.batch_no)) {
      showError('Validation Error', 'Select either Employee or Batch No, not both.');
      return;
    }
    if (!formData.stock_code) {
      showError('Validation Error', 'Stock Code is required.');
      return;
    }
    if (!formData.qty || isNaN(formData.qty) || Number(formData.qty) <= 0) {
      showError('Validation Error', 'Quantity must be a positive number.');
      return;
    }
    if (!formData.weight || isNaN(formData.weight) || Number(formData.weight) <= 0) {
      showError('Validation Error', 'Weight must be a positive number.');
      return;
    }
    
    // Debug logging
    console.log('Form data being submitted:', formData);
    console.log('User object:', user);
    console.log('Created by value:', user?.username || user?.user?.username || '');
    
    try {
      if (editMode) {
        // Update mode
        const updateData = {
          ...formData,
          karatage: formData.karatage_id || (purities.find(p => p.name === formData.karatage)?.id || null),
          color: formData.color_id || (colors.find(c => c.name === formData.color)?.id || null),
          qty: Number(formData.qty),
          weight: Number(formData.weight)
        };
        console.log('Update data:', updateData);
        await dispatch(updateStockReturn(initialData.id, updateData));
        showSuccess('Success', 'Stock return updated successfully.');
      } else {
        // Create mode
        const createData = {
          ...formData,
          karatage: formData.karatage_id || (purities.find(p => p.name === formData.karatage)?.id || null),
          color: formData.color_id || (colors.find(c => c.name === formData.color)?.id || null),
          qty: Number(formData.qty),
          weight: Number(formData.weight),
          created_by: user?.username || user?.user?.username || ''
        };
        console.log('Create data:', createData);
        await dispatch(addStockReturn(createData));
        showSuccess('Success', 'Stock returned successfully.');
        setFormData(initialForm);
        setSelectedStock(null);
      }
      if (onSuccess) onSuccess();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || (editMode ? 'Failed to update stock return.' : 'Failed to return stock.');
      showError('Error', errorMessage);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
        {editMode ? 'Edit Stock Return' : 'Stock Return'}
      </Typography>
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Autocomplete
            options={employees}
            getOptionLabel={option => option ? `${option.emp_code} - ${option.name}` : ''}
            value={employees.find(emp => emp.emp_code === formData.emp_code) || null}
            onChange={(_, value) => {
              setFormData(prev => ({ ...prev, emp_code: value ? value.emp_code : '', batch_no: '' }));
            }}
            disabled={!!formData.batch_no || editMode}
            renderInput={(params) => (
              <TextField {...params} label="Employee (search)" variant="outlined" />
            )}
            sx={{ minWidth: 200 }}
          />
          <Autocomplete
            options={availableBatches}
            getOptionLabel={option => option ? option.batch_number : ''}
            value={importedInventory.find(batch => batch.batch_number === formData.batch_no) || null}
            onChange={(_, value) => {
              setFormData(prev => ({ ...prev, batch_no: value ? value.batch_number : '', emp_code: '' }));
            }}
            disabled={!!formData.emp_code || editMode}
            renderInput={(params) => (
              <TextField {...params} label="Batch No (search)" variant="outlined" />
            )}
            sx={{ minWidth: 200 }}
          />
        </Box>
        <Autocomplete
          options={availableStocks}
          getOptionLabel={option => `${option.stock_code} - ${option.stock_name}`}
          value={selectedStock}
          onChange={handleStockCodeChange}
          disabled={editMode}
          renderInput={(params) => <TextField {...params} label="Stock Code" required />}
          sx={{ mb: 2, minWidth: 300 }}
        />
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Autocomplete
            options={stocks || []}
            getOptionLabel={option => option ? option.name : ''}
            value={stocks?.find(st => st.id === formData.stock_type_id) || null}
            onChange={(_, value) => setFormData(prev => ({ ...prev, stock_type_id: value ? value.id : null }))}
            renderInput={(params) => <TextField {...params} label="Stock Type" required />}
            sx={{ minWidth: 200 }}
            disabled={editMode}
          />
          <TextField label="Karatage" value={formData.karatage} InputProps={{ readOnly: true }} fullWidth />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField label="Color" value={formData.color} InputProps={{ readOnly: true }} fullWidth />
          <Autocomplete
            options={stones || []}
            getOptionLabel={option => option ? option.name : ''}
            value={stones?.find(st => st.id === formData.stone_type_id) || null}
            onChange={(_, value) => setFormData(prev => ({ ...prev, stone_type_id: value ? value.id : null }))}
            renderInput={(params) => <TextField {...params} label="Stone Type" />}
            sx={{ minWidth: 200 }}
            disabled={editMode || (formData.stock_type_id && stocks?.find(st => st.id === formData.stock_type_id)?.name?.toLowerCase() === 'gold')}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="Quantity"
            name="qty"
            value={formData.qty}
            onChange={handleChange}
            type="number"
            required
            fullWidth
          />
          <TextField
            label="Weight"
            name="weight"
            value={formData.weight}
            onChange={handleChange}
            type="number"
            required
            fullWidth
          />
        </Box>
        <TextField
          label="Return Reason"
          name="return_reason"
          value={formData.return_reason}
          onChange={handleChange}
          fullWidth
          sx={{ mb: 2 }}
        />
        {isCompletedBatch && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error" variant="body1">
              This batch is completed. Stock return is not allowed.
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button type="submit" variant="contained" color="primary" disabled={isCompletedBatch}>
            {editMode ? 'Update Stock Return' : 'Return Stock'}
          </Button>
          {editMode && onCancel && (
            <Button variant="outlined" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Box>
      </form>
    </Paper>
  );
};

export default StockReturnForm; 